import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CalendarDays } from 'lucide-react';
import { format } from 'date-fns';
import { api } from '@/lib/api';
import { TRIP } from '@/constants/testIds';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const SUPPORTED_CURRENCIES = ['AED', 'AUD', 'BRL', 'CAD', 'CHF', 'CZK', 'DKK', 'EUR', 'GBP', 'HKD', 'HUF', 'IDR', 'INR', 'JPY', 'KRW', 'MXN', 'MYR', 'NOK', 'NZD', 'PHP', 'PLN', 'SAR', 'SEK', 'SGD', 'THB', 'TRY', 'TWD', 'USD', 'VND', 'ZAR'];

function fmt(d) { return d ? format(d, 'PP') : 'Select date'; }

export default function NewTripDialog({ open, onOpenChange, onCreated }) {
    const navigate = useNavigate();
    const [name, setName] = useState('');
    const [destination, setDestination] = useState('');
    const [start, setStart] = useState(undefined);
    const [end, setEnd] = useState(undefined);
    const [currency, setCurrency] = useState('USD');
    const [startOpen, setStartOpen] = useState(false);
    const [endOpen, setEndOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const reset = () => {
        setName(''); setDestination(''); setStart(undefined); setEnd(undefined); setCurrency('USD');
    };

    const submit = async () => {
        if (!name || !destination || !start || !end) {
            toast.error('Please fill in all fields');
            return;
        }
        if (end < start) {
            toast.error('End date must be on or after start date');
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post('/trips', {
                name,
                destination,
                start_date: format(start, 'yyyy-MM-dd'),
                end_date: format(end, 'yyyy-MM-dd'),
                currency,
            });
            toast.success('Trip created!');
            reset();
            onOpenChange(false);
            onCreated?.(data.trip);
            navigate(`/trips/${data.trip.trip_id}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to create trip');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
            <DialogContent className="rounded-3xl max-w-md p-0 overflow-hidden border-[var(--ns-border)]" data-testid={TRIP.createDialog}>
                <div className="p-6 sm:p-8">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl font-semibold tracking-tight">New trip</DialogTitle>
                        <DialogDescription className="text-[var(--ns-text2)]">A few details to get started — you can edit later.</DialogDescription>
                    </DialogHeader>

                    <div className="mt-6 space-y-4">
                        <div>
                            <Label className="text-sm text-[var(--ns-text2)]">Trip name</Label>
                            <Input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="e.g. Riley's bachelor weekend"
                                data-testid={TRIP.nameInput}
                                className="h-12 rounded-xl mt-1 border-[var(--ns-border)] bg-white"
                            />
                        </div>
                        <div>
                            <Label className="text-sm text-[var(--ns-text2)]">Destination</Label>
                            <Input
                                value={destination}
                                onChange={(e) => setDestination(e.target.value)}
                                placeholder="City, Country"
                                data-testid={TRIP.destinationInput}
                                className="h-12 rounded-xl mt-1 border-[var(--ns-border)] bg-white"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-sm text-[var(--ns-text2)]">Start date</Label>
                                <Popover open={startOpen} onOpenChange={setStartOpen}>
                                    <PopoverTrigger asChild>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            data-testid={TRIP.startDateButton}
                                            className="w-full h-12 rounded-xl mt-1 border-[var(--ns-border)] justify-start font-normal bg-white"
                                        >
                                            <CalendarDays className="mr-2 h-4 w-4 text-[var(--ns-text3)]" />
                                            {fmt(start)}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="p-0 rounded-2xl z-[9999]" side="top" align="start" sideOffset={4}>
                                        <Calendar mode="single" selected={start} onSelect={(d) => { setStart(d); setStartOpen(false); }} initialFocus />
                                    </PopoverContent>
                                </Popover>
                            </div>
                            <div>
                                <Label className="text-sm text-[var(--ns-text2)]">End date</Label>
                                <Popover open={endOpen} onOpenChange={setEndOpen}>
                                    <PopoverTrigger asChild>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            data-testid={TRIP.endDateButton}
                                            className="w-full h-12 rounded-xl mt-1 border-[var(--ns-border)] justify-start font-normal bg-white"
                                        >
                                            <CalendarDays className="mr-2 h-4 w-4 text-[var(--ns-text3)]" />
                                            {fmt(end)}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="p-0 rounded-2xl z-[9999]" side="top" align="start" sideOffset={4}>
                                        <Calendar mode="single" selected={end} onSelect={(d) => { setEnd(d); setEndOpen(false); }} initialFocus />
                                    </PopoverContent>
                                </Popover>
                            </div>
                        </div>
                        <div>
                            <Label className="text-sm text-[var(--ns-text2)]">Trip currency</Label>
                            <Select value={currency} onValueChange={setCurrency}>
                                <SelectTrigger data-testid="trip-currency-select" className="h-12 rounded-xl mt-1 border-[var(--ns-border)] bg-white">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="rounded-2xl">
                                    {SUPPORTED_CURRENCIES.map((c) => (
                                        <SelectItem key={c} value={c}>{c}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-[var(--ns-text3)] mt-1">Used in the Budget tab. Owner can change later.</p>
                        </div>
                    </div>

                    <DialogFooter className="mt-8">
                        <Button
                            onClick={submit}
                            disabled={loading}
                            data-testid={TRIP.submitButton}
                            className="w-full h-12 rounded-full text-white font-medium"
                            style={{ background: 'var(--ns-navy)' }}
                        >
                            {loading ? 'Creating…' : 'Create trip'}
                        </Button>
                    </DialogFooter>
                </div>
            </DialogContent>
        </Dialog>
    );
}
